import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Send, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssignmentsPage() {
  const { user, getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    course_id: '',
    assignment_content: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const coursesRes = await axios.get(`${API_URL}/api/courses`, { headers });
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id) {
      toast.error('Please select a course');
      return;
    }
    if (!formData.assignment_content.trim()) {
      toast.error('Please enter your assignment content');
      return;
    }

    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/assignments`, {
        full_name: formData.full_name,
        email: formData.email,
        course_id: formData.course_id,
        assignment_content: formData.assignment_content
      }, { headers });
      
      toast.success('Assignment submitted successfully!');
      setFormData(prev => ({
        ...prev,
        course_id: '',
        assignment_content: ''
      }));
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit assignment';
      toast.error(typeof message === 'string' ? message : 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const selectableCourses = useMemo(() => courses.filter(course => !course.is_capstone), [courses]);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 p-8 bg-background">
          <div className="animate-pulse">Loading...</div>
        </main>
      </div>
    );
  }

  const isVisitor = user?.role === 'visitor';
  const isDoctor = user?.program === 'doctor';
  const isMdppm = user?.program === 'mdppm';
  const programName = isDoctor ? 'Doctor of Humanitarian Action & Development' : isMdppm ? 'Master of Development Project & Program Management' : 'Master of Humanitarian Action & Development';
  const levelWord = isDoctor ? "doctoral" : "master's";
  const numCourses = isDoctor ? 'eight' : isMdppm ? 'five' : 'nine';

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="assignments-page">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Visitor Banner */}
        {isVisitor && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="visitor-banner">
            <p className="text-amber-800 text-sm font-medium">You are viewing this page as a visitor. Form submissions are disabled.</p>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Assignments
          </h1>
          <p className="text-muted-foreground">
            Submit your course assignments and track their status
          </p>
        </div>

        <Tabs defaultValue="guidance" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="guidance" data-testid="tab-guidance">
              <BookOpen className="w-4 h-4 mr-2" />
              Assignment Guidance
            </TabsTrigger>
            <TabsTrigger value="submit" data-testid="tab-submit">
              <Send className="w-4 h-4 mr-2" />
              Submit Assignment
            </TabsTrigger>
          </TabsList>

          {/* Submit Assignment Tab */}
          <TabsContent value="submit">
            <Card className="border border-border shadow-none max-w-2xl" data-testid="assignment-form">
              <CardHeader>
                <CardTitle className="text-xl font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <FileText className="w-5 h-5" />
                  Submit Your Assignment
                </CardTitle>
                <CardDescription>
                  Fill in the form below to submit your assignment. You can paste your full assignment content in the text area.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-sm font-medium">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="full_name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      data-testid="full-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@gihds.org"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="email-input"
                    />
                  </div>

                  {/* Course Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="course" className="text-sm font-medium">
                      Select Course <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.course_id}
                      onValueChange={(value) => setFormData({ ...formData, course_id: value })}
                    >
                      <SelectTrigger data-testid="course-select">
                        <SelectValue placeholder="Choose the course for this assignment" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectableCourses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.order}. {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Assignment Content */}
                  <div className="space-y-2">
                    <Label htmlFor="assignment_content" className="text-sm font-medium">
                      Assignment Content <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="assignment_content"
                      placeholder="Paste your complete assignment here. You can include long text content..."
                      value={formData.assignment_content}
                      onChange={(e) => setFormData({ ...formData, assignment_content: e.target.value })}
                      required
                      rows={15}
                      className="min-h-[300px] font-mono text-sm resize-y"
                      data-testid="assignment-content-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.assignment_content.length} characters
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={submitting || isVisitor}
                    className="w-full h-11"
                    data-testid="submit-assignment-button"
                  >
                    {submitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Assignment
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Assignment Guidance Tab */}
          <TabsContent value="guidance">
            <Card className="border border-border shadow-none" data-testid="guidance-section">
              <CardHeader>
                <CardTitle className="text-xl font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <BookOpen className="w-5 h-5" />
                  Course Assignment Description
                </CardTitle>
                <CardDescription>
                  {programName} — Geneva Institute of Humanitarian & Development Studies (GIHDS)
                </CardDescription>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <div className="space-y-8">
                  {/* Assignment Overview */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Assignment Overview</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      As part of each course in the {programName}, students are required to complete one written assignment designed to demonstrate their understanding of the course content and their ability to apply that knowledge in a thoughtful, analytical, and professional manner.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3">
                      This assignment is not based on multiple-choice questions. Instead, students are expected to engage critically with the course materials and present their understanding in their own words through structured academic writing. The purpose of this assignment is to assess not only whether the student has followed the course content, but also whether they can interpret key concepts, connect them to humanitarian and development practice, and express informed reflections and judgments at a {levelWord} level.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3 font-medium">
                      Each course assignment serves as the principal written assessment for that course and must be completed individually.
                    </p>
                  </section>

                  {/* Purpose of the Assignment */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Purpose of the Assignment</h3>
                    <p className="text-muted-foreground mb-3">The course assignment is intended to help students:</p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                      <li>demonstrate a clear understanding of the core concepts, principles, frameworks, and debates presented in the course;</li>
                      <li>show their ability to connect theory to real humanitarian and development contexts;</li>
                      <li>apply course learning to practical, institutional, operational, ethical, or policy-related situations;</li>
                      <li>reflect critically on challenges, limitations, and implications for practice;</li>
                      <li>develop graduate-level academic writing and analytical skills.</li>
                    </ul>
                    <p className="text-muted-foreground mt-3 italic">
                      Students are expected to move beyond summary alone. A strong assignment should show evidence of understanding, interpretation, application, and critical thinking.
                    </p>
                  </section>

                  {/* Assignment Format */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Assignment Format</h3>
                    <p className="text-muted-foreground mb-3">For each course, students must submit one individual written assignment based on the content of that course.</p>
                    <div className="bg-muted/50 p-4 rounded-sm">
                      <p className="font-medium text-primary mb-2">Recommended Length: 1,000 to 1,500 words</p>
                      <p className="font-medium text-primary mb-2">Format Requirements:</p>
                      <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                        <li>Typed in English</li>
                        <li>Clear paragraph structure</li>
                        <li>Formal academic writing style</li>
                        <li>Original work prepared individually by the student</li>
                      </ul>
                    </div>
                  </section>

                  {/* General Assignment Expectation */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>General Assignment Expectation</h3>
                    <p className="text-muted-foreground mb-3">
                      In each course assignment, students are expected to write a structured response in their own words that demonstrates their understanding of the course and its relevance to humanitarian action and development practice.
                    </p>
                    <p className="text-muted-foreground mb-2">Depending on the course topic, students may be asked to do one or more of the following:</p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                      <li>explain the key ideas and concepts covered in the course;</li>
                      <li>analyze an issue, challenge, or debate related to the course;</li>
                      <li>apply course concepts to a real or realistic humanitarian/development context;</li>
                      <li>reflect on practical implications for organizations, practitioners, policy, communities, or affected populations;</li>
                      <li>present a reasoned position supported by course content and relevant examples.</li>
                    </ul>
                    <p className="text-muted-foreground mt-3 font-medium">
                      The assignment must be analytical, coherent, and relevant to the course theme. Pure description or copied content will not be sufficient to meet {levelWord}-level expectations.
                    </p>
                  </section>

                  {/* Suggested Structure */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Suggested Structure for Students</h3>
                    <p className="text-muted-foreground mb-3">Students are encouraged to organize their assignment using the following structure:</p>
                    <div className="space-y-4">
                      <div className="bg-primary/5 p-4 rounded-sm border-l-4 border-primary">
                        <p className="font-semibold text-primary">1. Introduction</p>
                        <p className="text-muted-foreground text-sm mt-1">Briefly introduce the topic, assignment focus, and overall purpose of the response.</p>
                      </div>
                      <div className="bg-primary/5 p-4 rounded-sm border-l-4 border-primary">
                        <p className="font-semibold text-primary">2. Understanding of Key Course Concepts</p>
                        <p className="text-muted-foreground text-sm mt-1">Explain the most important ideas, principles, models, standards, or frameworks covered in the course in a clear and accurate way.</p>
                      </div>
                      <div className="bg-primary/5 p-4 rounded-sm border-l-4 border-primary">
                        <p className="font-semibold text-primary">3. Analysis and Application</p>
                        <p className="text-muted-foreground text-sm mt-1">Discuss how these concepts relate to humanitarian or development practice. This may include operational realities, organizational challenges, ethical questions, policy concerns, or field-level implications.</p>
                      </div>
                      <div className="bg-primary/5 p-4 rounded-sm border-l-4 border-primary">
                        <p className="font-semibold text-primary">4. Critical Reflection</p>
                        <p className="text-muted-foreground text-sm mt-1">Reflect on the limitations, tensions, trade-offs, or practical difficulties connected to the course topic. Students should show independent thinking and not only repeat the course material.</p>
                      </div>
                      <div className="bg-primary/5 p-4 rounded-sm border-l-4 border-primary">
                        <p className="font-semibold text-primary">5. Conclusion</p>
                        <p className="text-muted-foreground text-sm mt-1">Summarize the main learning points and present a concise final reflection or position.</p>
                      </div>
                    </div>
                  </section>

                  {/* Submission Guidance */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Submission Guidance</h3>
                    <p className="text-muted-foreground mb-3">Students should ensure that their assignment:</p>
                    <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                      <li>directly addresses the task or question provided for the course;</li>
                      <li>is written in their own words;</li>
                      <li>is logically organized and easy to follow;</li>
                      <li>reflects understanding of the course content;</li>
                      <li>includes relevant examples where appropriate;</li>
                      <li>maintains academic honesty and originality.</li>
                    </ul>
                    <p className="text-destructive/80 mt-3 text-sm italic">
                      Assignments that are overly descriptive, poorly structured, off-topic, or lacking analysis may receive low marks even if they are lengthy.
                    </p>
                  </section>

                  {/* Academic Integrity */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Academic Integrity</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Students must submit original work. Copying from external sources, AI-generated content without proper academic ownership and review, or submitting work written by another person is not acceptable.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3">
                      Students may use learning tools to support understanding, note-taking, or language improvement, but the final submission must reflect the student's own understanding, judgment, and writing. GIHDS expects students to uphold the principles of honesty, integrity, and academic responsibility in all submitted work.
                    </p>
                  </section>

                  {/* Assessment Criteria */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Assessment Criteria</h3>
                    <p className="text-muted-foreground mb-4">Each course assignment will be assessed using the following four criteria:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-muted/30 p-4 rounded-sm">
                        <p className="font-semibold text-primary">1. Understanding of Key Concepts</p>
                        <p className="text-muted-foreground text-sm mt-1">The extent to which the student accurately understands and explains the main ideas, principles, and frameworks covered in the course.</p>
                      </div>
                      <div className="bg-muted/30 p-4 rounded-sm">
                        <p className="font-semibold text-primary">2. Application to Practice</p>
                        <p className="text-muted-foreground text-sm mt-1">The extent to which the student can connect course learning to practical realities, institutional settings, field operations, policy concerns, or relevant contexts.</p>
                      </div>
                      <div className="bg-muted/30 p-4 rounded-sm">
                        <p className="font-semibold text-primary">3. Critical Analysis</p>
                        <p className="text-muted-foreground text-sm mt-1">The extent to which the student goes beyond description and demonstrates reflection, interpretation, judgment, and analytical thinking.</p>
                      </div>
                      <div className="bg-muted/30 p-4 rounded-sm">
                        <p className="font-semibold text-primary">4. Clarity & Writing Quality</p>
                        <p className="text-muted-foreground text-sm mt-1">The extent to which the assignment is clearly written, logically structured, coherent, grammatically sound, and professionally presented.</p>
                      </div>
                    </div>
                  </section>

                  {/* Grading Scale */}
                  <section>
                    <h3 className="text-lg font-semibold text-primary border-b border-border pb-2 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Swiss Grading Scale</h3>
                    <p className="text-muted-foreground mb-4">GIHDS uses the Swiss 6-point grading scale for assessment.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-primary text-primary-foreground">
                            <th className="p-3 text-left">Grade</th>
                            <th className="p-3 text-left">Level</th>
                            <th className="p-3 text-left">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-border">
                            <td className="p-3 font-semibold text-green-700">6.0</td>
                            <td className="p-3">Excellent</td>
                            <td className="p-3 text-muted-foreground">Outstanding understanding, very strong application, excellent critical analysis, highly professional writing. Exceeds {levelWord}-level standards.</td>
                          </tr>
                          <tr className="border-b border-border bg-muted/30">
                            <td className="p-3 font-semibold text-green-600">5.5</td>
                            <td className="p-3">Very Good</td>
                            <td className="p-3 text-muted-foreground">Very strong understanding, clear application, good analytical depth, high standard of writing. Minor weaknesses may be present.</td>
                          </tr>
                          <tr className="border-b border-border">
                            <td className="p-3 font-semibold text-blue-600">5.0</td>
                            <td className="p-3">Good</td>
                            <td className="p-3 text-muted-foreground">Solid understanding, relevant application, satisfactory analysis. Clear writing, though some areas may lack depth.</td>
                          </tr>
                          <tr className="border-b border-border bg-muted/30">
                            <td className="p-3 font-semibold text-amber-600">4.5</td>
                            <td className="p-3">Satisfactory</td>
                            <td className="p-3 text-muted-foreground">Meets expected standard at acceptable level. Basic to moderate understanding, some practical relevance, limited analytical effort.</td>
                          </tr>
                          <tr className="border-b border-border">
                            <td className="p-3 font-semibold text-orange-600">4.0</td>
                            <td className="p-3">Pass</td>
                            <td className="p-3 text-muted-foreground">Meets minimum passing requirement. Basic understanding but may be weak in application, analysis, organization, or clarity.</td>
                          </tr>
                          <tr className="bg-red-50">
                            <td className="p-3 font-semibold text-red-600">&lt;4.0</td>
                            <td className="p-3">Fail</td>
                            <td className="p-3 text-muted-foreground">Does not meet required standard. Major weaknesses in understanding, relevance, analysis, structure, originality, or academic writing.</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* Important Note */}
                  <section className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
                    <h3 className="text-lg font-semibold text-amber-800 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Important Note to Students</h3>
                    <p className="text-amber-900">
                      A passing assignment is not achieved by length alone. Students are expected to demonstrate clear understanding, relevant application, and critical engagement with the course material. Strong assignments are those that show thoughtful interpretation, practical relevance, and clear academic writing.
                    </p>
                  </section>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
}
